import { useEffect } from "react";

import { DashboardWindow } from "./dashboardHelpers";

export type CoeffConsoleLevel = "debug" | "log" | "info" | "warn" | "error";

export type CoeffRenderPhase = "render_block" | "parse_helpers" | "slicer_init" | "unknown";

export type CoeffConsoleEvent = {
  seq: number;
  ts_ms: number;
  level: CoeffConsoleLevel;
  message: string;
  stack?: string;
};

function truncate(s: string, maxChars: number): string {
  if (s.length <= maxChars) {
    return s;
  }
  return s.slice(0, maxChars) + "…";
}

function isErrorLike(
  value: unknown
): value is { name?: unknown; message?: unknown; stack?: unknown } {
  if (!value || typeof value !== "object") {
    return false;
  }
  const v = value as any;
  return typeof v.message === "string" || typeof v.stack === "string" || typeof v.name === "string";
}

function safeStringify(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }
  // Cross-realm Errors (e.g. SyntaxError from iframe Function compilation) often
  // fail `instanceof Error` checks and stringify as `{}` because properties are
  // non-enumerable. Handle them via duck-typing.
  if (isErrorLike(value)) {
    const name = typeof value.name === "string" ? value.name : "Error";
    const message = typeof value.message === "string" ? value.message : "";
    return message ? `${name}: ${message}` : name;
  }
  try {
    const seen = new WeakSet<object>();
    return JSON.stringify(
      value,
      (_k, v) => {
        if (typeof v === "object" && v !== null) {
          if (seen.has(v as object)) {
            return "[Circular]";
          }
          seen.add(v as object);
        }
        return v;
      },
      0
    );
  } catch {
    return String(value);
  }
}

function stringifyConsoleArgs(args: unknown[]): { message: string; stack?: string } {
  const parts: string[] = [];
  let stack: string | undefined;

  for (const a of args) {
    if (!stack) {
      if (a instanceof Error && a.stack) {
        stack = a.stack;
      } else if (isErrorLike(a) && typeof a.stack === "string") {
        stack = a.stack;
      }
    }
    parts.push(safeStringify(a));
  }

  return { message: parts.join(" "), stack };
}

export function useDashboardConsoleCapture(targetWindow: DashboardWindow | null): void {
  useEffect(() => {
    if (!targetWindow) {
      return;
    }

    // Some TS lib configs don't model Window.console; ensure it's present in the local type.
    const w = targetWindow as DashboardWindow & { console: Console };

    if (targetWindow.__coeffConsoleCaptureInstalled) {
      return;
    }

    targetWindow.__coeffConsoleCaptureInstalled = true;
    targetWindow.__coeffConsoleSeq = 0;
    targetWindow.__coeffConsoleBuffer = [];

    const MAX_EVENTS = 200;
    const MAX_MESSAGE_CHARS = 2000;
    const MAX_STACK_CHARS = 6000;

    const originalConsole = {
      debug: w.console.debug.bind(w.console),
      log: w.console.log.bind(w.console),
      info: w.console.info.bind(w.console),
      warn: w.console.warn.bind(w.console),
      error: w.console.error.bind(w.console),
    };

    const pushEvent = (level: CoeffConsoleLevel, args: unknown[], stackOverride?: string): void => {
      const seq = (targetWindow.__coeffConsoleSeq = (targetWindow.__coeffConsoleSeq ?? 0) + 1);
      const { message, stack } = stringifyConsoleArgs(args);

      const ev: CoeffConsoleEvent = {
        seq,
        ts_ms: Date.now(),
        level,
        message: truncate(message, MAX_MESSAGE_CHARS),
      };

      const effectiveStack = stackOverride ?? stack;
      if (effectiveStack) {
        ev.stack = truncate(effectiveStack, MAX_STACK_CHARS);
      }

      const buf = targetWindow.__coeffConsoleBuffer ?? [];
      buf.push(ev);
      if (buf.length > MAX_EVENTS) {
        buf.splice(0, buf.length - MAX_EVENTS);
      }
      targetWindow.__coeffConsoleBuffer = buf;
    };

    // Wrap console methods (preserve original behavior).
    w.console.debug = (...args: unknown[]) => {
      pushEvent("debug", args);
      originalConsole.debug(...args);
    };
    w.console.log = (...args: unknown[]) => {
      pushEvent("log", args);
      originalConsole.log(...args);
    };
    w.console.info = (...args: unknown[]) => {
      pushEvent("info", args);
      originalConsole.info(...args);
    };
    w.console.warn = (...args: unknown[]) => {
      pushEvent("warn", args);
      originalConsole.warn(...args);
    };
    w.console.error = (...args: unknown[]) => {
      pushEvent("error", args);
      originalConsole.error(...args);
    };

    const onError = (event: ErrorEvent) => {
      const msg = event.message || "Uncaught error";
      const stack = event.error?.stack as string | undefined;
      pushEvent("error", [msg], stack);
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const stack =
        reason instanceof Error
          ? reason.stack
          : isErrorLike(reason) && typeof reason.stack === "string"
          ? reason.stack
          : undefined;
      pushEvent("error", ["Unhandled promise rejection:", reason], stack);
    };

    targetWindow.addEventListener("error", onError);
    targetWindow.addEventListener("unhandledrejection", onUnhandledRejection);

    targetWindow.__coeffConsoleCaptureCleanup = () => {
      // Restore console
      w.console.debug = originalConsole.debug;
      w.console.log = originalConsole.log;
      w.console.info = originalConsole.info;
      w.console.warn = originalConsole.warn;
      w.console.error = originalConsole.error;

      targetWindow.removeEventListener("error", onError);
      targetWindow.removeEventListener("unhandledrejection", onUnhandledRejection);

      delete targetWindow.__coeffConsoleCaptureInstalled;
      delete targetWindow.__coeffConsoleCaptureCleanup;
      // Keep buffer/seq around for postmortem access if needed.
    };

    return () => {
      // Only cleanup if we are still the active installer
      targetWindow.__coeffConsoleCaptureCleanup?.();
    };
  }, [targetWindow]);
}
